import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../../layout/Layout';
import customerService, { Customer } from '../../../services/customerService';
import InvoiceView from '../../invoice/InvoiceView';
import QuoteView from '../../quote/QuoteView';
import backButtonIcon from '../../../assets/backButton.png';
import cust from '../../../assets/cust.png';
import customerEditIcon from '../../../assets/customerEditIcon.png';
import supplierBulkUploadIcon from '../../../assets/supplierBulkUploadIcon.png';
import Pagination from '../../common/Pagination';
import { AUTH_TOKEN_KEY, API_URL, ENDPOINTS } from '../../../config';
import {
  BackButtonComponent,
  SearchContainer,
  SearchIconWrapper,
  SearchField
} from '../../ui/DesignSystem';
import PageLoadingSpinner from '../../ui/PageLoadingSpinner';
import CustomerHistoryBulkUploadModal from '../CustomerHistoryBulkUploadModal';

// Styled components
const PageContainer = styled.div`
  padding: 0 24px 24px 24px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 8px;
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: 32px;
  font-weight: 600;
  margin: 0;
`;

const PageWrapper = styled.div`
  background: white;
  margin: 0;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`;

const ContentContainer = styled.div`
  padding: 0 0 24px 0;
  flex: 1;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0.75rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #042B41;
  font-weight: 500;

  img {
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  font-size: 28px;
  font-weight: 600;

  span {
    margin: 0 8px;
  }
`;

const BreadcrumbLink = styled(Link)`
  color: #9CA3AF;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #6B7280;
  }
`;

const BreadcrumbCurrent = styled.span`
  color: #042B41;
`;

const SearchActionSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.25rem;
  padding: 0;
`;

const BulkUploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 12px;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: #0A3D5A;
  }
`;

// Template link style - used for download links
const TemplateLink = styled.a`
  color: #0088cc;
  text-decoration: none;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }

  svg {
    margin-right: 8px;
  }
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  border: 1px solid #e0e0e0;
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;

  th {
    padding: 8px 12px;
    text-align: center;
    font-weight: 500;
    border: 1px solid #0A3D5A;
    vertical-align: middle;
    
    &:nth-child(4) { /* Grand Total column */
      text-align: center;
    }
    
    &:last-child { /* Actions column */
      text-align: center;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    background-color: white;
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f9fafb;
    }
  }

  td {
    padding: 8px 12px;
    color: #333;
    border: 1px solid #e0e0e0;
    vertical-align: middle;
    text-align: center;
    
    &:nth-child(4) { /* Grand Total column */
      text-align: right;
      font-weight: 500;
    }
    
    &:last-child { /* Actions column */
      text-align: center;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const DisabledActionButton = styled.div`
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: not-allowed;

  img {
    width: 20px;
    height: 20px;
  }
`;

// Add a SearchBar component
const CustomerSearchContainer = styled(SearchContainer)`
  width: 100%;
  max-width: 500px;
  margin-bottom: 0;
  
  input {
    padding: 8px 12px 8px 2.5rem;
    font-size: 14px;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
`;

interface CustomerHistory {
  id: string;
  number: string; // Invoice/Quote number
  storeType: string;
  date: string;
  total: number;
  notes?: boolean;
  type: 'invoice' | 'quote';
  invoiceId?: string; // Add invoice ID for opening InvoiceView modal
  quoteId?: string; // Add quote ID for opening QuoteView modal
  isBulkUploaded?: boolean;
}

// Memoized History Row component to prevent unnecessary re-renders
const HistoryRow = React.memo(({ item, onViewInvoice, onViewQuote, onEditInvoice, onEditQuote }: { 
  item: CustomerHistory;
  onViewInvoice: (invoiceId: string) => void;
  onViewQuote: (quoteId: string) => void;
  onEditInvoice: (invoiceId: string) => void;
  onEditQuote: (quoteId: string) => void;
}) => {
  const handlePrint = () => {
    if (item.type === 'invoice' && item.invoiceId) {
      onViewInvoice(item.invoiceId);
    } else if (item.type === 'quote' && item.quoteId) {
      onViewQuote(item.quoteId);
    } else {
      console.error('No valid ID found for item:', item);
      // Fallback: try using the main id field
      if (item.type === 'invoice') {
        onViewInvoice(item.id);
      } else {
        onViewQuote(item.id);
      }
    }
  };

  const handleEdit = () => {
    if (item.type === 'invoice' && item.invoiceId) {
      onEditInvoice(item.invoiceId);
    } else if (item.type === 'quote' && item.quoteId) {
      onEditQuote(item.quoteId);
    } else {
      console.error('No valid ID found for editing item:', item);
      // Fallback: try using the main id field
      if (item.type === 'invoice') {
        onEditInvoice(item.id);
      } else {
        onEditQuote(item.id);
      }
    }
  };

  return (
    <tr key={item.id}>
      <td>{typeof item.number === 'object' ? 
          JSON.stringify(item.number) : 
          String(item.number)}</td>
      <td>{typeof item.storeType === 'object' ? 
          JSON.stringify(item.storeType) : 
          String(item.storeType)}</td>
      <td>{typeof item.date === 'object' ? 
          JSON.stringify(item.date) : 
          String(item.date)}</td>
      <td>
        {typeof item.total === 'number' 
          ? `$${item.total.toFixed(2)}` 
          : typeof item.total === 'object'
            ? `$${JSON.stringify(item.total)}`
            : `$${item.total}`}
      </td>
      <td>
        <ActionsCell>
          {/* Check if this is a bulk uploaded item and disable the buttons if it is */}
          {item.isBulkUploaded ? (
            <>
              <DisabledActionButton title="Edit not available for bulk uploaded items">
                <img src={customerEditIcon} alt="Edit" style={{ opacity: 0.5 }} />
              </DisabledActionButton>
              <DisabledActionButton title="Print not available for bulk uploaded items">
                <img src={cust} alt="Print" style={{ opacity: 0.5 }} />
              </DisabledActionButton>
            </>
          ) : (
            <>
              <ActionButton title="Edit" onClick={handleEdit}>
                <img src={customerEditIcon} alt="Edit" />
              </ActionButton>
              <ActionButton title="Print Details" onClick={handlePrint}>
                <img src={cust} alt="Print" />
              </ActionButton>
            </>
          )}
        </ActionsCell>
      </td>
    </tr>
  );
});

// Memoized Empty State component
const EmptyState = React.memo(() => (
  <tr>
    <td colSpan={5} style={{ textAlign: 'center', padding: '32px' }}>
      No history found for this customer.
    </td>
  </tr>
));

// Custom hook for debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CustomerHistoryPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [historyItems, setHistoryItems] = useState<CustomerHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Add state for invoice and quote modals
  const [isViewInvoiceOpen, setIsViewInvoiceOpen] = useState(false);
  const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
  const [isViewQuoteOpen, setIsViewQuoteOpen] = useState(false);
  const [viewQuoteId, setViewQuoteId] = useState<string | null>(null);
  
  // Add a ref to track API calls in progress
  const apiCallInProgress = useRef<boolean>(false);
  
  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Memoize the fetch function to prevent unnecessary rerenders
  const fetchData = useCallback(async () => {
    // Check if API call is already in progress
    if (apiCallInProgress.current) return;
    
    apiCallInProgress.current = true;
    setLoading(true);

    try {
      // Ensure customerId is a string
      if (!customerId) {
        console.error('No customer ID provided');
        setHistoryItems([]);
        setTotalPages(1);
        return;
      }

      // Fetch customer details
      let customer = null;
      try {
        console.log(`Fetching customer details for ID: ${customerId}`);
        
        // Get token from local storage for direct API call
        const token = localStorage.getItem('auth_token');
        
        // Make a direct API call to get the customer data
        const response = await fetch(`${API_URL}/api/v1/customers/${customerId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          credentials: 'include',
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Customer data from API:', data);

        if (data && data.company_name) {
          // Create a customer object with the correct name
          const customerData: Customer = {
            id: data.id || customerId,
            name: data.company_name,
            contactPerson: data.contact_person || '',
            email: data.email || '',
            phone: data.phone || '',
            billingAddress: data.billing_address || '',
            billingSuburb: data.billing_suburb || '',
            billingPostcode: data.billing_postcode || '',
            isAccountCustomer: data.is_account || false,
            priceList: data.price_list ? data.price_list.name : null,
            price_list_id: data.price_list_id || '',
            status: 'active',
            createdDate: data.created_at || '',
            lastOrderDate: null
          };

          console.log('Setting customer with name:', customerData.name);
          setCustomer(customerData);
        } else {
          console.error('API response missing company_name:', data);
          throw new Error('Customer data is incomplete');
        }
      } catch (customerError) {
        console.error('Error fetching customer directly:', customerError);
        // Fall back to using the service if direct API call fails
        try {
          const fallbackCustomerData = await customerService.getCustomerById(customerId);
          setCustomer(fallbackCustomerData);
        } catch (fallbackError) {
          console.error('Even fallback customer fetch failed:', fallbackError);
          throw new Error('Failed to fetch customer data');
        }
      }

      // Fetch customer history using the API endpoint
      try {
        console.log(`Fetching history for customer ${customerId} (page ${currentPage}, per_page ${itemsPerPage}, search=${debouncedSearchQuery || 'none'})`);
        
        // Use the new pagination parameters with search
        const historyResponse = await customerService.getCustomerHistory(
          customerId,
          currentPage,
          itemsPerPage,
          debouncedSearchQuery
        );

        // Safety check for the response
        if (!historyResponse) {
          console.error('Received null or undefined response from history service');
          setHistoryItems([]);
          setTotalPages(1);
          return;
        }

        // Check if items array exists
        if (!historyResponse.items || !Array.isArray(historyResponse.items)) {
          console.error('History response missing items array:', historyResponse);
          setHistoryItems([]);
          setTotalPages(1);
          return;
        }

        // Log the raw history items to inspect the structure
        console.log(`Raw history items (${historyResponse.items.length} items):`, 
          JSON.stringify(historyResponse.items.slice(0, 3), null, 2) + 
          (historyResponse.items.length > 3 ? '... (truncated)' : ''));

        // Add performance measurement
        const startTime = performance.now();
        console.log("Starting response processing");

        // Transform the history data for UI format
        const transformedHistory = historyResponse.items.map((item: any) => {
          try {
            // Log each item during transformation
            console.log('Transforming item:', item);

            // Format date to DD-MM-YYYY
            let formattedDate = '';
            if (item.date) {
              formattedDate = new Date(item.date).toLocaleDateString('en-GB');
            } else if (item.invoice_date) {
              formattedDate = new Date(item.invoice_date).toLocaleDateString('en-GB');
            } else if (item.created_at) {
              formattedDate = new Date(item.created_at).toLocaleDateString('en-GB');
            } else {
              formattedDate = new Date().toLocaleDateString('en-GB');
            }

            // Determine type and extract appropriate IDs
            const isQuote = item.type === 'quote' || item.invoice_type === 'quote' || item.quote_number || item.quote_id;
            const itemType: 'invoice' | 'quote' = isQuote ? 'quote' : 'invoice';

            // Extract invoice/quote IDs from various possible fields
            let invoiceId = null;
            let quoteId = null;

            if (itemType === 'invoice') {
              invoiceId = item.invoice_id || item.id || item.invoice_number_id;
            } else {
              quoteId = item.quote_id || item.id || item.quote_number_id;
            }

            // Safely extract properties with fallbacks
            return {
              id: item.id || `hist-${Math.random().toString(36).substring(2, 11)}`,
              // Check all possible field names for invoice/quote number
              number: item.number || item.invoice_number || item.invoice_no || item.quote_number || 'N/A',
              storeType: item.storeType || item.store_type_name || item.store_type || '',
              date: formattedDate,
              total: typeof item.total === 'number' ? item.total : 
                     (typeof item.grand_total === 'number' ? item.grand_total :
                     (item.amount || item.total_amount || 0)),
              notes: !!item.has_notes || !!item.notes,
              type: itemType,
              invoiceId: invoiceId,
              quoteId: quoteId,
              isBulkUploaded: !!item.is_bulk_uploaded
            };
          } catch (itemError) {
            console.error('Error transforming history item:', itemError, item);
            // Return a placeholder for problematic items
            return {
              id: `error-${Math.random().toString(36).substring(2, 11)}`,
              number: 'Error',
              storeType: '',
              date: new Date().toLocaleDateString('en-GB'),
              total: 0,
              notes: false,
              type: 'invoice' as const,
              invoiceId: null,
              quoteId: null,
              isBulkUploaded: false
            };
          }
        });

        console.log('Transformed history data:', transformedHistory);
        setHistoryItems(transformedHistory);
        
        // Use the pagination info from the API response
        const totalPages = historyResponse.pages || Math.max(Math.ceil((historyResponse.total || 0) / itemsPerPage), 1);
        console.log(`Setting total pages to ${totalPages} based on API response`);
        setTotalPages(totalPages);
        setTotalItems(historyResponse.total || 0);

      } catch (historyError) {
        console.error("Error fetching customer history:", historyError);
        setHistoryItems([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setHistoryItems([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  }, [customerId, currentPage, itemsPerPage, debouncedSearchQuery]);  // Include all dependencies except fetchData itself

  // Use the memoized fetch function in useEffect
  useEffect(() => {
    console.log('Initial fetchData call');
    fetchData();
  }, [customerId, currentPage, itemsPerPage]); // Remove fetchData from dependencies to avoid circular references
  
  // Add a separate effect to trigger fetch when search query changes
  useEffect(() => {
    console.log('Debounced search query changed to:', debouncedSearchQuery);
    // Only trigger if not the initial render (empty string)
    if (apiCallInProgress.current) {
      console.log('API call already in progress, will wait');
      return;
    }
    
    console.log('Triggering fetchData due to search query change');
    fetchData();
  }, [debouncedSearchQuery]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleBackToCustomers = () => {
    navigate('/customers');
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowBulkUploadModal(false);
  };
  
  const handleBulkUploadSuccess = () => {
    setShowBulkUploadModal(false);
    // Refresh the history data
    fetchData();
  };

  // No need for custom modal handlers as we're using the CustomerHistoryBulkUploadModal component

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Search query changed:', value);
    setSearchQuery(value);
    // The debounced search query will trigger the API call via the fetchData dependency
    setCurrentPage(1); // Reset to first page on new search
    
    // Add more detailed logging
    console.log('Search query set to:', value);
    console.log('Current API call in progress:', apiCallInProgress.current);
  };

  // Handle opening invoice modal
  const handleViewInvoice = useCallback((invoiceId: string) => {
    console.log('Opening invoice modal for ID:', invoiceId);
    setViewInvoiceId(invoiceId);
    setIsViewInvoiceOpen(true);
  }, []);

  // Handle closing invoice modal
  const handleCloseInvoice = () => {
    console.log('Closing invoice modal');
    setIsViewInvoiceOpen(false);
    setViewInvoiceId(null);
  };

  // Handle opening quote modal
  const handleViewQuote = useCallback((quoteId: string) => {
    console.log('Opening quote modal for ID:', quoteId);
    setViewQuoteId(quoteId);
    setIsViewQuoteOpen(true);
  }, []);

  // Handle closing quote modal
  const handleCloseQuote = () => {
    console.log('Closing quote modal');
    setIsViewQuoteOpen(false);
    setViewQuoteId(null);
  };

  // Handle editing invoice
  const handleEditInvoice = useCallback((invoiceId: string) => {
    console.log('Navigating to edit invoice:', invoiceId);
    const returnTo = customerId ? `/customers/${customerId}/history` : null;
    if (returnTo) {
      navigate(`/invoices?edit=${invoiceId}&returnTo=${encodeURIComponent(returnTo)}`);
    } else {
      navigate(`/invoices?edit=${invoiceId}`);
    }
  }, [navigate, customerId]);

  // Handle editing quote
  const handleEditQuote = useCallback((quoteId: string) => {
    console.log('Navigating to edit quote:', quoteId);
    const returnTo = customerId ? `/customers/${customerId}/history` : null;
    if (returnTo) {
      navigate(`/create-quote?edit=${quoteId}&returnTo=${encodeURIComponent(returnTo)}`);
    } else {
      navigate(`/create-quote?edit=${quoteId}`);
    }
  }, [navigate, customerId]);

  // Memoize the history table to prevent unnecessary re-renders
  const historyTable = useMemo(() => (
    <HistoryTable>
      <TableHeader>
        <tr>
          <th>Invoice/Quote No</th>
          <th>Store Type</th>
          <th>Created Date</th>
          <th>Grand Total (AUD)</th>
          <th>Actions</th>
        </tr>
      </TableHeader>
      <TableBody>
        {historyItems.length > 0 ? (
          historyItems.map(item => (
            <HistoryRow 
              key={item.id} 
              item={item} 
              onViewInvoice={handleViewInvoice}
              onViewQuote={handleViewQuote}
              onEditInvoice={handleEditInvoice}
              onEditQuote={handleEditQuote}
            />
          ))
        ) : (
          <EmptyState />
        )}
      </TableBody>
    </HistoryTable>
  ), [historyItems, handleViewInvoice, handleViewQuote, handleEditInvoice, handleEditQuote]);

  if (loading && !customer) {
    return <PageLoadingSpinner message="Loading customer details" />;
  }

  return (
    <Layout>
      <PageWrapper>
        <ContentContainer>
          <TopSection>
            <TitleSection>
              <BackButton to="/customers">
                <img src={backButtonIcon} alt="Back" />
              </BackButton>

              <BreadcrumbContainer>
                <BreadcrumbLink to="/customers">Customers</BreadcrumbLink>
                <span>&gt;</span>
                <BreadcrumbCurrent>{customer?.name || 'Loading'}</BreadcrumbCurrent>
              </BreadcrumbContainer>
            </TitleSection>

            <SearchActionSection>
              <CustomerSearchContainer>
                <SearchIconWrapper>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </SearchIconWrapper>
                <SearchField
                  type="text"
                  placeholder="Search history"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </CustomerSearchContainer>

              <BulkUploadButton onClick={handleBulkUpload}>
                <img src={supplierBulkUploadIcon} alt="Bulk Upload" />
                Bulk Upload
              </BulkUploadButton>
            </SearchActionSection>
          </TopSection>

          {loading ? (
            <PageLoadingSpinner message="Loading customer history" />
          ) : (
            <>
              {historyTable}

              {totalPages > 1 && (
                <PaginationWrapper>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    itemsPerPageOptions={[10, 20, 50]}
                    showItemsPerPage={true}
                  />
                </PaginationWrapper>
              )}
            </>
          )}
        </ContentContainer>
      </PageWrapper>

      {/* Invoice View Modal */}
      {isViewInvoiceOpen && viewInvoiceId && (
        <InvoiceView
          invoiceId={viewInvoiceId}
          onClose={handleCloseInvoice}
        />
      )}

      {/* Quote View Modal */}
      {isViewQuoteOpen && viewQuoteId && (
        <QuoteView
          quoteId={viewQuoteId}
          onClose={handleCloseQuote}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <CustomerHistoryBulkUploadModal
          customerId={customerId || ''}
          onClose={handleCloseModal}
          onSuccess={handleBulkUploadSuccess}
        />

      )}
    </Layout>
  );
};

export default CustomerHistoryPage;